import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * RailCarousel — the right-rail is a swipeable stack of cards, one visible
 * at a time. Arrows + dots on the header. Keyboard: ← → cycle slides.
 * Height fills the rail pane; each slide gets a scroll container so long
 * content (alerts, phantom history) still works.
 *
 * Slides are declared by the parent so the carousel is pure UX and knows
 * nothing about alerts/demand/CCTV/phantom.
 */
import { useCallback, useEffect, useState } from "react";
const railStyle = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "var(--color-pure-white)",
    borderLeft: "1px solid var(--color-pebble)",
    minWidth: 0,
};
const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px 10px 18px",
    borderBottom: "1px solid var(--color-pebble)",
    gap: 12,
};
const titleBlockStyle = {
    minWidth: 0,
    flex: "1 1 auto",
};
const kickerStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
};
const titleStyle = {
    fontSize: 20,
    lineHeight: 1.1,
    fontWeight: 700,
    letterSpacing: "-0.2px",
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};
const navStyle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
};
const arrowStyle = {
    width: 28,
    height: 28,
    borderRadius: 999,
    border: "1px solid var(--color-pebble)",
    background: "var(--color-pure-white)",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: "var(--color-deep-ink, #000d10)",
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
};
const bodyStyle = {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
};
const dotsStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "10px 12px",
    borderTop: "1px solid var(--color-pebble)",
};
export function RailCarousel({ slides, initialIndex = 0, }) {
    const count = slides.length;
    const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0)));
    useEffect(() => {
        if (index > count - 1)
            setIndex(Math.max(count - 1, 0));
    }, [count, index]);
    const go = useCallback((delta) => setIndex((i) => (i + delta + count) % count), [count]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.target instanceof HTMLElement) {
                const tag = e.target.tagName;
                if (tag === "INPUT" || tag === "TEXTAREA")
                    return;
            }
            if (e.key === "ArrowRight")
                go(1);
            if (e.key === "ArrowLeft")
                go(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go]);
    if (count === 0) {
        return (_jsx("aside", { style: railStyle, "data-testid": "rail-carousel", "aria-label": "Rail", children: _jsx("div", { style: { padding: 20, color: "var(--color-cool-ash)" }, children: "No panels available." }) }));
    }
    const slide = slides[index];
    if (!slide) {
        return (_jsx("aside", { style: railStyle, "data-testid": "rail-carousel", "aria-label": "Rail", children: _jsx("div", { style: { padding: 20, color: "var(--color-cool-ash)" }, children: "Slide unavailable." }) }));
    }
    return (_jsxs("aside", { style: railStyle, "data-testid": "rail-carousel", "aria-label": "Rail", children: [_jsxs("div", { style: headerStyle, children: [_jsxs("div", { style: titleBlockStyle, children: [_jsxs("div", { style: kickerStyle, children: [_jsx("span", { children: `${index + 1} / ${count}` }), slide.badge ?? null] }), _jsx("h2", { style: titleStyle, children: slide.title }), slide.subtitle ? (_jsx("div", { style: {
                                    fontSize: 12,
                                    color: "var(--color-cool-ash)",
                                    marginTop: 3,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }, children: slide.subtitle })) : null] }), _jsxs("div", { style: navStyle, children: [_jsx("button", { type: "button", style: arrowStyle, "aria-label": "Previous panel", onClick: () => go(-1), "data-testid": "rail-prev", children: "\u2039" }), _jsx("button", { type: "button", style: arrowStyle, "aria-label": "Next panel", onClick: () => go(1), "data-testid": "rail-next", children: "\u203A" })] })] }), _jsx("div", { style: bodyStyle, "data-testid": `rail-slide-${slide.id}`, children: slide.content }), _jsx("div", { style: dotsStyle, role: "tablist", "aria-label": "Panel indicator", children: slides.map((s, i) => (_jsx("button", { type: "button", role: "tab", "aria-selected": i === index, "aria-label": s.title, onClick: () => setIndex(i), style: {
                        width: i === index ? 22 : 8,
                        height: 8,
                        borderRadius: 999,
                        border: "none",
                        background: i === index
                            ? "var(--color-clay-ember, #bc7155)"
                            : "var(--color-pebble, #d9d9de)",
                        cursor: "pointer",
                        padding: 0,
                        transition: "width 160ms ease",
                    } }, s.id))) })] }));
}
